import React, {forwardRef} from 'react';

import {FloatingPopover} from '@pb/kit-component-floating';
import {withCssModule} from '@pb/kit-util-with-css-module';

import {Body} from './body';
import styles from './dropdown.module.styl';

import type {UseDropdownReturnType} from './use-dropdown';
import type {FloatingPopoverProps} from '@pb/kit-component-floating';
import type {ComponentProps} from 'react';


const classNames = withCssModule(styles);

type DropdownPropsFromHook = ReturnType<UseDropdownReturnType['getDropdownProps']>;
type NativeProps = Omit<ComponentProps<'div'>, 'ref'>;

export interface DropdownBaseProps extends DropdownPropsFromHook, NativeProps {
    /**
     * Component that wraps `children` inside the floating panel. Defaults to the
     * built-in `<Body>` div. Override to add scrolling or a custom container.
     */
    bodyComponent?: typeof Body,

    /** Props forwarded to `FloatingPortal` (e.g. mount target, wrapper class). */
    portalProps?: FloatingPopoverProps['portalProps'],
    /** Props forwarded to Floating UI's `FloatingFocusManager`. */
    floatingFocusManagerProps?: FloatingPopoverProps['floatingFocusManagerProps'],

    /**
     * When `false`, keeps the dropdown content mounted in the DOM while hidden.
     * @deprecated It will be removed in 6 months 09-15-2025. render your modals outside of the dropdown, see example in the Storybook
     */
    shouldUnmount?: boolean,
}


/**
 * Low-level floating panel that renders `children` inside a `FloatingPopover`.
 * Use with `useDropdown` to obtain the required `getContext`, `listItemsRef`,
 * and `arrow` props. Prefer the higher-level `<Dropdown>` component unless you
 * need to compose anchor and panel separately.
 *
 * @see [Storybook]{@link https://storybook.dev.pitchbookdata.com/?path=/docs/hover-dropdown--documentation}
 */
export const DropdownBase = forwardRef<HTMLDivElement, DropdownBaseProps>(function DropdownBase(props, ref) {
    const {
        bodyComponent: BodyComponent = Body,
        children,
        getContext,
        listItemsRef,
        portalProps,
        arrow: Arrow,
        floatingFocusManagerProps,
        shouldUnmount = true,
        ...nativeProps
    } = props;

    const context = getContext();

    const floatingClasses = classNames(nativeProps.className, 'dropdown');

    return (
        <FloatingPopover
            {...nativeProps}
            open={!shouldUnmount}
            className={floatingClasses}
            floatingFocusManagerProps={{
                order: ['floating', 'content'],
                ...floatingFocusManagerProps,
            }}
            flip={context.placement.startsWith('top-')}
            listItemsRef={listItemsRef}
            portalProps={portalProps}
            ref={ref}
            getContext={getContext}>
            <Arrow getContext={getContext}/>
            <BodyComponent>
                {children}
            </BodyComponent>
        </FloatingPopover>
    );
});

DropdownBase.displayName = 'DropdownBase';
